import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TABS = ["overview", "bookings", "orders", "leads"];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/admin/studio-bookings").then((r) => setBookings(r.data)).catch(() => {});
      api.get("/admin/prop-orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="pt-32 text-center">Loading…</div>;
  if (user === false || user?.role !== "admin") return <Navigate to="/login" replace />;

  const updateBooking = async (id, status) => {
    try {
      await api.patch(`/admin/studio-bookings/${id}`, null, { params: { status } });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Updated");
    } catch { toast.error("Failed"); }
  };
  const updateOrder = async (id, status) => {
    try {
      await api.patch(`/admin/prop-orders/${id}`, null, { params: { status } });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Updated");
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-page" className="pt-24">
      <section className="container mx-auto px-4 pt-12 pb-24">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-2">Admin Panel</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-8">CFT Control Room</h1>

        <div className="flex gap-2 border-b border-zinc-200 mb-6 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition capitalize ${
                tab === t ? "border-[#FF007F] text-[#FF007F]" : "border-transparent text-zinc-500"
              }`}>{t}</button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(stats).map(([k, v]) => (
              <div key={k} className="bg-white border border-zinc-200 rounded-2xl p-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{k.replace(/_/g, " ")}</div>
                <div className="font-display font-black text-4xl text-gradient">{v}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left">
                <tr><th className="p-3">Studio</th><th className="p-3">Customer</th><th className="p-3">Date/Slot</th><th className="p-3">Total</th><th className="p-3">Status</th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-zinc-200" data-testid={`admin-booking-${b.id}`}>
                    <td className="p-3 font-bold">{b.studio_name}</td>
                    <td className="p-3">{b.full_name}<br /><span className="text-xs text-zinc-500">{b.email}</span></td>
                    <td className="p-3">{b.booking_date}<br /><span className="text-xs text-zinc-500">{b.time_slot} · {b.hours}h</span></td>
                    <td className="p-3">₹{b.total_amount.toLocaleString()}</td>
                    <td className="p-3">
                      <select value={b.status} onChange={(e) => updateBooking(b.id, e.target.value)}
                        className="text-xs px-2 py-1 border border-zinc-300 rounded">
                        {["pending", "confirmed", "cancelled", "completed"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td className="p-6 text-center text-zinc-500" colSpan={5}>No bookings yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "orders" && (
          <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left">
                <tr><th className="p-3">#</th><th className="p-3">Customer</th><th className="p-3">Items</th><th className="p-3">Total</th><th className="p-3">Status</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-zinc-200" data-testid={`admin-order-${o.id}`}>
                    <td className="p-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                    <td className="p-3">{o.full_name}<br /><span className="text-xs text-zinc-500">{o.email}</span></td>
                    <td className="p-3 text-xs">{o.items.length} items</td>
                    <td className="p-3">₹{o.total.toLocaleString()}</td>
                    <td className="p-3">
                      <select value={o.status} onChange={(e) => updateOrder(o.id, e.target.value)}
                        className="text-xs px-2 py-1 border border-zinc-300 rounded">
                        {["pending", "confirmed", "shipped", "returned", "cancelled"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td className="p-6 text-center text-zinc-500" colSpan={5}>No orders yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "leads" && (
          <div className="grid md:grid-cols-2 gap-3">
            {leads.map((l) => (
              <div key={l.id} className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid={`admin-lead-${l.id}`}>
                <div className="flex justify-between mb-2">
                  <div className="font-bold">{l.name}</div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100">{l.subject}</span>
                </div>
                <div className="text-xs text-zinc-500 mb-2">{l.email} · {l.phone}</div>
                <p className="text-sm">{l.message}</p>
              </div>
            ))}
            {leads.length === 0 && <div className="col-span-2 text-center text-zinc-500 py-12">No leads yet</div>}
          </div>
        )}
      </section>
    </div>
  );
}
